import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Divider,
  Box,
  TableContainer,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

const fmtDate = (d) => {
  if (!d) return 'N/A';
  const date = new Date(d);
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const getFormDescription = (appointment) => {
  const formType = appointment?.patientFormId?.formType;
  const formData = appointment?.patientFormId?.formData;

  if (!formType || !formData) return '-';

  switch (formType) {
    case 'PHYSIO':
      return formData?.description?.trim() || '-';

    case 'DENTAL':
      return (
        formData?.dentalQuestions?.specificConcern?.trim() ||
        formData?.dentalQuestions?.skinConcern?.trim() ||
        '-'
      );

    case 'ESTHETIC':
      return (
        formData?.estheticsQuestions?.skinConcern?.trim() ||
        formData?.estheticsQuestions?.specificConcern?.trim() ||
        '-'
      );

    default:
      return '-';
  }
};


export default function SessionSummaryDialog({ open, onClose, appointment }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  // const sessions = appointment?.sessions || [];
  const sessions = useMemo(() => {
  return (appointment?.sessions || []).filter(
    (s) => s.isSessionTaken === true
  );
}, [appointment]);

  // const assessmentFee = appointment?.patientFormId?.payment || 0;
  const rawAssessmentFee = appointment?.patientFormId?.payment;

  const assessmentFee = rawAssessmentFee === 'FOC' ? 0 : Number(rawAssessmentFee || 0);

  const totals = useMemo(() => {
    const totalPayment = sessions.reduce((s, it) => s + Number(it.payment || 0), 0);
    const totalPaid = sessions.reduce((s, it) => s + Number(it.paidAmount || 0), 0);
    const totalRemaining = sessions.reduce(
      (s, it) =>
        s + Number(it.remainingAmount ?? Math.max(0, (it.payment || 0) - (it.paidAmount || 0))),
      0,
    );
    return { totalPayment, totalPaid, totalRemaining };
  }, [sessions]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 2 },
          border: '1px solid var(--color-border-soft)',
          overflow: 'hidden',
        },
      }}
      sx={{
        '@media print': {
          '.MuiDialogActions-root': {
            display: 'none',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          py: 1.1,
          px: 1.5,
          fontSize: '1rem',
          fontWeight: 700,
          color: 'var(--color-heading)',
          borderBottom: '1px solid var(--color-border-soft)',
          background: '#f8fbff',
        }}
      >
        Session Summary
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 1.1, sm: 1.5 } }}>
        <Grid container spacing={1.2} sx={{ mb: 1.2 }}>
          <Grid item xs={12} md={6}>
            <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--color-heading)' }}>
              Patient
            </Typography>
            <Typography>
              {appointment?.patient?.name || appointment?.patientId?.name || 'N/A'}
            </Typography>
            <Typography variant="caption">
              Phone: {appointment?.patient?.phone || appointment?.patientId?.phone || 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--color-heading)' }}>
              Doctor
            </Typography>
            <Typography>
              {appointment?.doctor?.name || appointment?.doctorId?.name || 'N/A'}
            </Typography>
            <Typography variant="caption">
              Treatment:{' '}
              {appointment?.treatment ||
                appointment?.patientFormId?.formData?.treatment ||
                appointment?.patientFormId?.formData?.dentalQuestions?.specificConcern ||
                appointment?.patientFormId?.formData?.estheticsQuestions?.skinConcern ||
                'N/A'}
            </Typography>
          </Grid>
        </Grid>

        <Paper
          variant="outlined"
          sx={{
            p: 1.2,
            mb: 1.25,
            border: '1px solid var(--color-border-soft)',
            background: 'linear-gradient(90deg, #f7f9ff, #ffffff)',
          }}
        >
          <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, mb: 0.4 }}>
            Assessment Fee
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* <Typography sx={{ fontSize: 20, fontWeight: 700 }}>₹ {assessmentFee}</Typography> */}
            <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
              {rawAssessmentFee === 'FOC' ? 'FOC' : `₹ ${assessmentFee}`}
            </Typography>

            <Typography color="text.secondary">(Included in overall totals)</Typography>
          </Box>
        </Paper>

        <Typography sx={{ mb: 0.7, fontSize: '0.9rem', fontWeight: 700, color: 'var(--color-heading)' }}>
          Sessions
        </Typography>

        <TableContainer
          component={Paper}
          sx={{
            mb: 1.25,
            border: '1px solid var(--color-border-soft)',
            borderRadius: 1.25,
            maxHeight: { xs: '44vh', sm: '52vh' },
            overflow: 'auto',
            '& .MuiTableCell-root': { fontSize: '0.78rem', py: 0.6, whiteSpace: 'nowrap' },
            '& .MuiTableHead-root .MuiTableCell-root': {
              fontWeight: 700,
              color: 'var(--color-heading)',
              backgroundColor: '#f8fbff',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            },
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 200 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Payment</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Paid</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Remaining</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No sessions
                  </TableCell>
                </TableRow>
              )}
              {sessions.map((s, idx) => (
                <TableRow key={idx} hover>
                  <TableCell>{s.sessionNo || idx + 1}</TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 260,
                      minWidth: 200,
                      whiteSpace: 'normal !important',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    <Typography variant="body2">{getFormDescription(appointment)}</Typography>
                  </TableCell>
                  <TableCell>{fmtDate(s.sessionDate)}</TableCell>
                  <TableCell>₹ {s.payment || 0}</TableCell>
                  <TableCell>₹ {s.paidAmount || 0}</TableCell>
                  <TableCell>
                    ₹ {s.remainingAmount ?? Math.max(0, (s.payment || 0) - (s.paidAmount || 0))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 1.1 }} />

        <Grid container spacing={1.2}>
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 1.1, borderColor: 'var(--color-border-soft)' }}>
              <Typography sx={{ fontSize: '0.76rem', fontWeight: 700 }}>Sessions Total</Typography>
              <Typography sx={{ fontSize: '1rem', fontWeight: 800 }}>₹ {totals.totalPayment}</Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 1.1, borderColor: 'var(--color-border-soft)' }}>
              <Typography sx={{ fontSize: '0.76rem', fontWeight: 700 }}>Paid (Sessions)</Typography>
              <Typography sx={{ fontSize: '1rem', fontWeight: 800 }}>₹ {totals.totalPaid}</Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 1.1, borderColor: 'var(--color-border-soft)' }}>
              <Typography sx={{ fontSize: '0.76rem', fontWeight: 700 }}>Remaining (Sessions)</Typography>
              <Typography sx={{ fontSize: '1rem', fontWeight: 800 }}>₹ {totals.totalRemaining}</Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6} sx={{ mt: 0.4 }}>
            <Paper variant="outlined" sx={{ p: 1.1, background: '#f3f6ff', borderColor: 'var(--color-border-soft)' }}>
              <Typography sx={{ fontSize: '0.76rem', fontWeight: 700 }}>Overall Total (Assessment + Sessions)</Typography>
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 800 }}>
                ₹{Number(assessmentFee) + Number(totals.totalPayment)}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6} sx={{ mt: 0.4 }}>
            <Paper variant="outlined" sx={{ p: 1.1, background: '#fff7f0', borderColor: 'var(--color-border-soft)' }}>
              <Typography sx={{ fontSize: '0.76rem', fontWeight: 700 }}>Overall Remaining</Typography>
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 800 }}>
                ₹ {totals.totalRemaining}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 1.25, py: 1, borderTop: '1px solid var(--color-border-soft)', gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: 'none',
            minHeight: 32,
            borderRadius: '8px',
            borderColor: 'var(--color-border)',
            color: 'var(--color-heading)',
            fontWeight: 600,
            '&:hover': {
              borderColor: 'var(--color-primary)',
              backgroundColor: 'var(--color-bg-soft)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => window.print()}
          className="dialogSubmitBtn"
          sx={{
            textTransform: 'none',
            minHeight: 32,
            borderRadius: '8px',
            color: 'var(--button-text)',
            boxShadow: 'none',
            fontWeight: 700,
          }}
        >
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
}
